import { castArray, isEmpty, maxBy, partition, sortBy } from 'lodash-es';
import { Fragment, PropsWithChildren, ReactElement, useCallback, useMemo, useRef, useState } from 'react';

import { PanelInstanceContextProvider } from './PanelInstanceContextProvider';
import { OpenPanelOptions, PanelInstance, PanelsContext } from './PanelsContext';
import { PanelPosition } from './types';
import { PanelStateOptions } from './types/PanelStateOptions';
import { v4 } from '../common/uuid';
import { useInterval } from '../common/useInterval';

export interface PanelsContextProviderProps {
  baseZIndex?: number;
  bounds?: string | Element;
  panelStateOptions?: PanelStateOptions | null;
  tilingStart?: PanelPosition;
  tilingOffset?: PanelPosition;
  tilingMax?: PanelPosition;
}

const defaultPanelStateOptions: PanelStateOptions = {
  saveStateIn: 'localStorage',
  saveStateKey: 'userId',
};

export const PanelsContextProvider = ({
  bounds,
  baseZIndex = 500,
  children,
  panelStateOptions: _panelStateOptions,
  tilingStart = { x: 30, y: 30 },
  tilingOffset = { x: 30, y: 50 },
  tilingMax = { x: 200, y: 300 },
}: PropsWithChildren<PanelsContextProviderProps>): ReactElement => {
  const panelStateOptions = _panelStateOptions === undefined ? defaultPanelStateOptions : _panelStateOptions;

  const stackPositionRef = useRef(tilingStart);

  // Can't use a map here as we need to retain render order for performance
  const [panelInstances, setPanelInstances] = useState<PanelInstance[]>([]);

  const [dockElements, setDockElements] = useState<Record<string, HTMLDivElement>>({});

  const setDockElement = useCallback(
    (id: string, element: HTMLDivElement) => {
      if (dockElements[id] !== element) {
        setDockElements({ ...dockElements, [id]: element });
      }
    },
    [dockElements],
  );

  const bringPanelToFront = useCallback(
    (panelUniqueId: string) => {
      setPanelInstances((panelInstances): PanelInstance[] => {
        const panelInstance = panelInstances.find((panelInstance) => panelInstance.uniqueId === panelUniqueId);
        if (!panelInstance) {
          console.warn(`bringPanelToFront cannot find panel with uniqueId: ${panelUniqueId}`);
          return panelInstances;
        }
        if (panelInstance.window) {
          panelInstance.window.focus();
          return panelInstances;
        }
        const maxZIndexPanelInstance = maxBy(panelInstances, 'zIndex');
        // Prevent unnecessary state updates
        if (maxZIndexPanelInstance === panelInstance) {
          return panelInstances;
        }
        sortBy(panelInstances, (pi) => (panelInstance === pi ? 32768 : pi.zIndex)).forEach(
          (pi, i) => (pi.zIndex = baseZIndex + i),
        );
        return [...panelInstances];
      });
    },
    [baseZIndex],
  );

  /**
   * @param showHidePanelIds PanelIds to show or hide, if null show/hide all panels.
   */
  const showHidePanel = useCallback((showHidePanelIds: null | string | string[], hidden: boolean) => {
    setPanelInstances((panelInstances) => {
      const panelIds = showHidePanelIds === null ? null : castArray(showHidePanelIds);
      return panelInstances.map((pi) => {
        return panelIds === null || (panelIds.includes(pi.uniqueId) && pi.hidden !== hidden) ? { ...pi, hidden } : pi;
      });
    });
  }, []);

  const hidePanel = useCallback(
    (hidePanelIds: string | string[]) => {
      showHidePanel(hidePanelIds, true);
    },
    [showHidePanel],
  );

  const hideAllPanels = useCallback(() => {
    showHidePanel(null, true);
  }, [showHidePanel]);

  const unhidePanel = useCallback(
    (hidePanelIds: string | string[]) => {
      showHidePanel(hidePanelIds, false);
    },
    [showHidePanel],
  );

  const unhideAllPanels = useCallback(() => {
    showHidePanel(null, false);
  }, [showHidePanel]);

  const hasHiddenPanel = useCallback(
    (panelId: string) => {
      return panelInstances.find((pi) => pi.uniqueId === panelId)?.hidden === true;
    },
    [panelInstances],
  );

  const openPanel = useCallback(
    ({ componentFn, poppedOut = false, uniqueId = v4(), onClose }: OpenPanelOptions): string | null => {
      try {
        setPanelInstances((panelInstances) => {
          const existingPanelInstance = panelInstances.find((pi) => pi.uniqueId === uniqueId);
          if (existingPanelInstance) {
            if (existingPanelInstance.window) {
              existingPanelInstance.window?.focus();
            } else {
              if (existingPanelInstance.hidden) {
                setTimeout(() => unhidePanel(uniqueId), 0);
              }
              setTimeout(() => bringPanelToFront(uniqueId), 0);
            }
            return panelInstances;
          }

          return [
            ...panelInstances,
            {
              uniqueId,
              componentInstance: componentFn(),
              zIndex: baseZIndex + panelInstances.length,
              poppedOut,
              window: null,
              onClose,
              hidden: false,
            },
          ];
        });
        return uniqueId;
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    [baseZIndex, bringPanelToFront, unhidePanel],
  );

  const closePanel = useCallback((closePanelUniqueIds: string | string[]) => {
    const panelIds = castArray(closePanelUniqueIds);
    setPanelInstances((panelInstances) => {
      const [closedPanelInstances, stillOpenPanelInstances] = partition(panelInstances, (pi) =>
        panelIds.includes(pi.uniqueId),
      );
      if (!isEmpty(closedPanelInstances)) {
        // Defer onClose (consumers commonly setState/navigate in there) out of this
        // render-phase updater - calling it synchronously here triggers React's
        // "Cannot update a component while rendering a different component" warning.
        setTimeout(() => {
          closedPanelInstances.forEach(({ onClose }) => {
            onClose?.();
          });
        }, 0);
        return stillOpenPanelInstances;
      }
      return panelInstances;
    });
  }, []);

  /**
   * It's not easy to tell the difference between a window closing and a window popping in via events,
   * so we're periodically checking instead.
   */
  useInterval(() => {
    // close any panels that have a window that is closed
    closePanel(panelInstances.filter((pi) => pi.window?.closed).map((panel) => panel.uniqueId));
  }, 500);

  /**
   * Tile the panel position relative to previous panel
   */
  const nextStackPosition = useCallback(() => {
    const p = { ...stackPositionRef.current };
    const result = { ...p };
    p.x += tilingOffset.x;
    p.y += tilingOffset.y;
    stackPositionRef.current = p.x > tilingMax.x || p.y > tilingMax.y ? tilingStart : p;
    return result;
  }, [tilingMax.x, tilingMax.y, tilingOffset.x, tilingOffset.y, tilingStart]);

  const openPanels = useMemo(() => new Set(panelInstances.map((pi) => pi.uniqueId)), [panelInstances]);

  const hasOpenPanel = useCallback((panelId: string): boolean => openPanels.has(panelId), [openPanels]);

  return (
    <PanelsContext.Provider
      value={{
        openPanels,
        hasOpenPanel,
        openPanel,
        closePanel,
        hasHiddenPanel,
        bringPanelToFront,
        nextStackPosition,
        dockElements,
        setDockElement,
        panelStateOptions,
        hidePanel,
        hideAllPanels,
        unhidePanel,
        unhideAllPanels,
      }}
    >
      <Fragment key={'panels'}>
        {panelInstances.map((panelInstance) => (
          <PanelInstanceContextProvider
            key={panelInstance.uniqueId}
            panelInstance={panelInstance}
            bounds={bounds}
            hidden={panelInstance.hidden}
          >
            {panelInstance.componentInstance}
          </PanelInstanceContextProvider>
        ))}
      </Fragment>
      <Fragment key={'children'}>{children}</Fragment>
    </PanelsContext.Provider>
  );
};
